import React, { useState, useEffect, useCallback } from "react"
import { Plus, Pencil, Trash2, QrCode, ExternalLink, Link2, Users, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import { useQrCampaigns, useDeleteQrCampaign } from "src/features/qr_campaigns/hooks/useQrCampaigns"
import { QrCampaignDrawer } from "src/features/qr_campaigns/components/QrCampaignDrawer"
import { STATUS_MAP, MANAGER_ROLE_MAP } from "src/features/qr_campaigns/constants"

function initials(name) {
  if (!name) return "?"
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?"
}

export default function QrCodesPage() {
  const { toast } = useToast()
  const [q, setQ] = useState("")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const { data, loading, fetch } = useQrCampaigns()
  const deleteHook = useDeleteQrCampaign()

  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const reload = useCallback(() => fetch({ q }), [fetch, q])
  useEffect(() => {
    const t = setTimeout(reload, 250)
    return () => clearTimeout(t)
  }, [reload])

  const campaigns = data?.qr_campaigns ?? []

  const sortedCampaigns = React.useMemo(() => {
    if (!sortField || !sortDirection) return campaigns
    return [...campaigns].sort((a, b) => {
      let aVal, bVal
      if (sortField === "label") {
        aVal = a.label || ""
        bVal = b.label || ""
      } else if (sortField === "project") {
        aVal = a.project?.name || ""
        bVal = b.project?.name || ""
      } else if (sortField === "assigned_to") {
        aVal = a.assigned_to?.name || ""
        bVal = b.assigned_to?.name || ""
      } else if (sortField === "leads") {
        aVal = a.lead_count || 0
        bVal = b.lead_count || 0
      } else if (sortField === "status") {
        aVal = STATUS_MAP[a.status]?.label || a.status || ""
        bVal = STATUS_MAP[b.status]?.label || b.status || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [campaigns, sortField, sortDirection])

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${className}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  function openCreate() { setEditingId(null); setDrawerOpen(true) }
  function openEdit(c) { setEditingId(c.id); setDrawerOpen(true) }

  async function handleDelete(c) {
    if (!window.confirm(`Delete QR campaign "${c.label}"? This cannot be undone.`)) return
    try {
      await deleteHook.mutate(c.id)
      toast({ title: "Campaign deleted", description: c.label })
      reload()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not delete campaign"
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-bold text-slate-900">QR Codes</h2>
          <p className="text-[13px] text-slate-500">
            Public micro-sites for events and campaigns that capture leads via a QR code.
          </p>
        </div>
        <Button size="sm" className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New QR code
        </Button>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by label or subdomain…"
        className="max-w-[320px]"
      />

      {loading && !data ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : campaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-16 text-center">
          <QrCode className="h-10 w-10 text-slate-300" />
          <div>
            <p className="font-medium text-slate-700">No QR campaigns yet</p>
            <p className="text-[13px] text-slate-500">Create one to generate a public lead-capture micro-site.</p>
          </div>
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New QR code
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Label & subdomain", "label")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Project", "project")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Assigned to", "assigned_to")}</TableHead>
                <TableHead className="text-right text-black">{renderSortableHeader("Leads", "leads", "w-full justify-end")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("State", "status")}</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCampaigns.map((c, idx) => {
                const status = STATUS_MAP[c.status]
                return (
                  <TableRow key={c.id} className="group">
                    <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{c.label}</div>
                      <a
                        href={c.public_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Link2 className="h-3 w-3" />
                        {c.subdomain}
                      </a>
                    </TableCell>
                    <TableCell className="text-slate-600">{c.project?.name || "—"}</TableCell>
                    <TableCell>
                      {c.assigned_to ? (
                        <span className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-[11px] font-medium text-sky-800">
                            {c.assigned_to.initials || initials(c.assigned_to.name)}
                          </span>
                          <span className="text-[13px] text-slate-600">
                            {MANAGER_ROLE_MAP[c.manager_role] || c.assigned_to.name}
                          </span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-[13px] text-slate-400">
                          <Users className="h-3.5 w-3.5" /> Unassigned
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-700">{c.lead_count}</TableCell>
                    <TableCell>
                      {status && (
                        <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${status.badge}`}>
                          {status.label}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        <a href={c.public_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" title="Open micro-site">
                            <ExternalLink className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                          </Button>
                        </a>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Edit">
                          <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(c)} title="Delete">
                          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <QrCampaignDrawer
        open={drawerOpen}
        campaignId={editingId}
        onClose={() => setDrawerOpen(false)}
        onSuccess={reload}
      />
    </div>
  )
}
